import { FC, useState, useEffect, useMemo, useRef } from 'react';
import { Modal } from '../components/Modal';
import { Input, Select, Button, CopyableInput } from '../components/Form';
import { SortableHeader } from '../components/SortableHeader';
import { useSorting } from '../hooks/useSorting';
import {
  useGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useGetOrganizationsSelectQuery,
  useGetRolesSelectQuery,
  UserCreateRequest,
  UserUpdateRequest,
  UserListResponse,
} from '../api/api';

const UserForm: FC<{
  user?: UserListResponse;
  onSubmit: (data: UserCreateRequest | UserUpdateRequest) => void;
  onCancel: () => void;
}> = ({ user, onSubmit, onCancel }) => {
  const { data: organizations } = useGetOrganizationsSelectQuery();
  const { data: roles } = useGetRolesSelectQuery();

  // Find initial organization and role based on user data
  const initialOrg = organizations?.find(o => o.name === user?.organization_name);

  const [formData, setFormData] = useState<UserCreateRequest | UserUpdateRequest>({
    email: user?.email || '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    role_id: user?.role_id || '',
    role_name: user?.role_name || '',
    organization_id: initialOrg?._id || '',
    organization_name: user?.organization_name || '',
  });

  // Update form data when organizations or roles data is loaded
  useEffect(() => {
    if (user && organizations && !formData.organization_id) {
      const org = organizations.find(o => o.name === user.organization_name);
      if (org) {
        setFormData(prev => ({
          ...prev,
          organization_id: org._id,
          organization_name: org.name
        }));
      }
    }
  }, [user, organizations, formData.organization_id]);

  useEffect(() => {
    if (user && roles && !formData.role_id) {
      const role = roles.find(r => r._id === user.role_id);
      if (role) {
        setFormData(prev => ({
          ...prev,
          role_id: role._id,
          role_name: role.name
        }));
      }
    }
  }, [user, roles, formData.role_id]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {user && (
        <CopyableInput
          label="ID"
          value={user.id}
        />
      )}
      <Input
        label="Email"
        type="email"
        value={formData.email}
        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        required
      />
      <Input
        label="First Name"
        value={formData.first_name}
        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
        required
      />
      <Input
        label="Last Name"
        value={formData.last_name}
        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
        required
      />
      <Select
        label="Organization"
        value={formData.organization_id}
        onChange={(e) => {
          const org = organizations?.find((o) => o._id === e.target.value);
          setFormData({
            ...formData,
            organization_id: e.target.value,
            organization_name: org?.name || '',
          });
        }}
        options={organizations || []}
        required
      />
      <Select
        label="Role"
        value={formData.role_id}
        onChange={(e) => {
          const role = roles?.find((r) => r._id === e.target.value);
          setFormData({
            ...formData,
            role_id: e.target.value,
            role_name: role?.name || '',
          });
        }}
        options={roles || []}
        required
      />
      <div className="flex justify-end space-x-2">
        <Button type="button" onClick={onCancel} className="bg-gray-600">
          Cancel
        </Button>
        <Button type="submit">{user ? 'Update' : 'Create'}</Button>
      </div>
    </form>
  );
};

export const Users: FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserListResponse | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('All Roles');
  const [selectedOrg, setSelectedOrg] = useState<string>('All Organizations');
  const [uploadError, setUploadError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: users, isLoading } = useGetUsersQuery();
  const { data: organizations } = useGetOrganizationsSelectQuery();
  const { data: roles } = useGetRolesSelectQuery();
  const [createUser] = useCreateUserMutation();
  const [updateUser] = useUpdateUserMutation();

  // Get unique roles and organizations for filters
  const uniqueRoles = useMemo(() => {
    if (!users) return [];
    return ['All Roles', ...new Set(users.map(user => user.role_name))];
  }, [users]);

  const uniqueOrgs = useMemo(() => {
    if (!users) return [];
    return ['All Organizations', ...new Set(users.map(user => user.organization_name))];
  }, [users]);

  // Filter users based on search term and filters
  const filteredUsers = useMemo(() => {
    if (!users) return [];
    
    return users.filter(user => {
      const matchesSearch = searchTerm === '' || 
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase());
        
      const matchesRole = selectedRole === 'All Roles' || user.role_name === selectedRole;
      const matchesOrg = selectedOrg === 'All Organizations' || user.organization_name === selectedOrg;

      return matchesSearch && matchesRole && matchesOrg;
    });
  }, [users, searchTerm, selectedRole, selectedOrg]);

  const { items: sortedUsers, requestSort, sortConfig } = useSorting<UserListResponse>(filteredUsers);

  const handleSubmit = async (data: UserCreateRequest | UserUpdateRequest) => {
    try {
      if (selectedUser) {
        await updateUser({ id: selectedUser.id, body: data as UserUpdateRequest });
      } else {
        await createUser(data as UserCreateRequest);
      }
      setIsModalOpen(false);
      setSelectedUser(undefined);
    } catch (error) {
      console.error('Error saving user:', error);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError('');
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n');
        const headers = lines[0].split(',').map(h => h.trim());
        
        // Validate headers
        const requiredHeaders = ['first_name', 'last_name', 'email', 'organization_name', 'role_name'];
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
        if (missingHeaders.length > 0) {
          throw new Error(`Missing required headers: ${missingHeaders.join(', ')}`);
        }

        // Get header indices
        const headerIndices = {
          first_name: headers.indexOf('first_name'),
          last_name: headers.indexOf('last_name'),
          email: headers.indexOf('email'),
          organization_name: headers.indexOf('organization_name'),
          role_name: headers.indexOf('role_name'),
        };

        // Process each line
        const users: UserCreateRequest[] = [];
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          const values = line.split(',').map(v => v.trim());
          const organization = organizations?.find(o => o.name === values[headerIndices.organization_name]);
          const role = roles?.find(r => r.name === values[headerIndices.role_name]);

          if (!organization) {
            throw new Error(`Organization not found: ${values[headerIndices.organization_name]}`);
          }
          if (!role) {
            throw new Error(`Role not found: ${values[headerIndices.role_name]}`);
          }

          users.push({
            first_name: values[headerIndices.first_name],
            last_name: values[headerIndices.last_name],
            email: values[headerIndices.email],
            organization_id: organization._id,
            organization_name: organization.name,
            role_id: role._id,
            role_name: role.name,
          });
        }

        // Create users
        for (const user of users) {
          await createUser(user);
        }

        setIsUploadModalOpen(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        setUploadError(error instanceof Error ? error.message : 'Failed to process CSV file');
      }
    };

    reader.readAsText(file);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Users</h1>
        <div className="space-x-2">
          <Button onClick={() => setIsUploadModalOpen(true)}>Upload CSV</Button>
          <Button onClick={() => setIsModalOpen(true)}>Create User</Button>
        </div>
      </div>

      <div className="mb-6 space-y-4">
        <div className="flex space-x-4">
          <div className="flex-1">
            <Input
              label="Search"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
          <div className="w-64">
            <Select
              label="Role"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              options={uniqueRoles.map(role => ({ _id: role, name: role }))}
              showEmptyOption={false}
            />
          </div>
          <div className="w-64">
            <Select
              label="Organization"
              value={selectedOrg}
              onChange={(e) => setSelectedOrg(e.target.value)}
              options={uniqueOrgs.map(org => ({ _id: org, name: org }))}
              showEmptyOption={false}
            />
          </div>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <SortableHeader<UserListResponse>
                label="Name"
                sortKey="first_name"
                currentSort={sortConfig}
                onSort={requestSort}
              />
              <SortableHeader<UserListResponse>
                label="Email"
                sortKey="email"
                currentSort={sortConfig}
                onSort={requestSort}
              />
              <SortableHeader<UserListResponse>
                label="Role"
                sortKey="role_name"
                currentSort={sortConfig}
                onSort={requestSort}
              />
              <SortableHeader<UserListResponse>
                label="Organization"
                sortKey="organization_name"
                currentSort={sortConfig}
                onSort={requestSort}
              />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedUsers?.map((user) => (
              <tr
                key={user.id}
                onClick={() => {
                  setSelectedUser(user);
                  setIsModalOpen(true);
                }}
                className="cursor-pointer hover:bg-gray-50"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  {user.first_name} {user.last_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{user.email}</td>
                <td className="px-6 py-4 whitespace-nowrap">{user.role_name}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {user.organization_name}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedUser(undefined);
        }}
        title={selectedUser ? 'Edit User' : 'Create User'}
      >
        <UserForm
          user={selectedUser}
          onSubmit={handleSubmit}
          onCancel={() => {
            setIsModalOpen(false);
            setSelectedUser(undefined);
          }}
        />
      </Modal>

      <Modal
        isOpen={isUploadModalOpen}
        onClose={() => {
          setIsUploadModalOpen(false);
          setUploadError('');
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }}
        title="Upload Users CSV"
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            <p>Please upload a CSV file with the following headers:</p>
            <code className="block mt-2 p-2 bg-gray-100 rounded">
              first_name,last_name,email,organization_name,role_name
            </code>
            <p className="mt-2">Example:</p>
            <code className="block mt-2 p-2 bg-gray-100 rounded">
              John,Doe,john.doe@example.com,Organization Name,author
            </code>
          </div>
          <Input
            label="CSV File"
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            ref={fileInputRef}
            className="cursor-pointer"
          />
          {uploadError && (
            <div className="text-sm text-red-600">
              {uploadError}
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              onClick={() => {
                setIsUploadModalOpen(false);
                setUploadError('');
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
              className="bg-gray-600"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}; 